import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, conditions, NOMBRES, seededRandom } from "../spec";

const SHEET = "LG Nómina";
const TABLE = "Nomina";

const rnd = seededRandom(1607);
// Empleado, Puesto, Antigüedad, Salario diario, Ventas, Meta, Horas extra, Evaluación
const ROWS = NOMBRES.slice(0, 30).map((nombre, i) => {
  const puesto = i % 5 === 4 ? "Administrativo" : i % 7 === 0 ? "Supervisor" : "Vendedor";
  const meta = puesto === "Administrativo" ? 0 : rnd.round(80000, 250000, 5000);
  const ventas = puesto === "Administrativo" ? 0 : Math.round((meta * rnd.int(70, 135)) / 100);
  const horas = rnd.next() < 0.35 ? 0 : rnd.int(1, 16);
  const evaluacion = i % 6 === 5 ? "" : rnd.pick(["A", "A", "B", "C"] as const);
  return [
    nombre,
    puesto,
    rnd.int(0, 15),
    rnd.round(350, 1200, 10),
    ventas,
    meta,
    horas,
    evaluacion,
  ] as [string, string, number, number, number, number, number, string];
});

type Row = (typeof ROWS)[number];
const horasExtra = (r: Row) => {
  const hora = r[3] / 8;
  return r[6] <= 9 ? r[6] * hora * 2 : 9 * hora * 2 + (r[6] - 9) * hora * 3;
};

export const spec: FuncionProjectSpec = {
  projectId: "funciones-anidadas",
  sheets: [
    {
      name: SHEET,
      title: "Nómina quincenal",
      subtitle: "Pre-nómina del área comercial — cada columna amarilla combina varias funciones",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: [
            "Empleado",
            "Puesto",
            "Antigüedad",
            "Salario diario",
            "Ventas",
            "Meta",
            "Horas extra",
            "Evaluación",
          ],
          rows: ROWS,
          answerColumns: [
            "Bono ventas",
            "Evaluación final",
            "Pago horas extra",
            "Prima antigüedad",
            "Productividad",
          ],
          formats: {
            "Salario diario": "$#,##0.00",
            Ventas: "$#,##0",
            Meta: "$#,##0",
            "Bono ventas": "$#,##0.00",
            "Pago horas extra": "$#,##0.00",
            "Prima antigüedad": "$#,##0.00",
          },
        },
      ],
    },
  ],
  checks: {
    1: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Bono ventas", fns: ["IF", "AND"],
      expected: ROWS.map((r) => (r[4] >= r[5] && r[7] === "A" ? r[4] * 0.03 : 0)),
    },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Evaluación final", fns: ["IF", "ISBLANK"],
      expected: ROWS.map((r) => (r[7] === "" ? "Pendiente" : r[7])),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Pago horas extra", fns: ["IFS"],
      expected: ROWS.map(horasExtra),
    },
    4: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Prima antigüedad", fns: ["IF", "OR", "AND"],
      expected: ROWS.map((r) => (r[2] >= 5 || (r[2] >= 3 && r[7] === "A") ? r[3] * 12 : 0)),
    },
    5: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Productividad", fns: ["IFERROR", "IFS"],
      expected: ROWS.map((r) => {
        if (r[5] === 0) return "Sin meta";
        const ratio = r[4] / r[5];
        return ratio >= 1.1 ? "Sobresaliente" : ratio >= 1 ? "Cumple" : "No cumple";
      }),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-anidadas",
  title: "Reto final: Nómina con funciones anidadas",
  subtitle: "Todo lo anterior, combinado",
  description:
    "Recursos Humanos te pide la pre-nómina del área comercial: bonos por ventas, horas extra según la ley, primas de antigüedad y un indicador de productividad.\n\nCada columna combina dos o más funciones de esta categoría. Si ya practicaste los proyectos anteriores, aquí demuestras que las dominas juntas.",
  topics: [
    "SI con Y",
    "SI con ESBLANCO",
    "SI.CONJUNTO con cálculos",
    "O con un Y dentro",
    "SI.ERROR envolviendo SI.CONJUNTO",
  ],
  tasks: [
    {
      id: 1,
      level: "intermedio",
      title: "Tarea 1: Bono por ventas",
      description:
        conditions(
        "En la hoja <strong>LG Nómina</strong>, columna <strong>Bono ventas</strong>. Hay bono del <strong>3%</strong> de las ventas solo si se cumplen <strong>las dos</strong> condiciones:",
        [
          "Las <strong>Ventas</strong> alcanzan la <strong>Meta</strong>",
          "La <strong>Evaluación</strong> es <strong>A</strong>",
        ],
        "Si no, el bono es <strong>0</strong>."
      ),
      solution: [
        `Selecciona ${F("I5")}.`,
        `Escribe ${F('=SI(Y([@Ventas]>=[@Meta],[@Evaluación]="A"),[@Ventas]*3%,0)')}.`,
      ],
      expectedResult: "3% de las ventas, o 0",
      finalFormula: '=SI(Y([@Ventas]>=[@Meta],[@Evaluación]="A"),[@Ventas]*3%,0)',
    },
    {
      id: 2,
      level: "intermedio",
      title: "Tarea 2: Evaluaciones pendientes",
      description:
        "En la columna <strong>Evaluación final</strong>: si la <strong>Evaluación</strong> está vacía escribe <strong>Pendiente</strong>; si no, repite la evaluación (A, B o C).",
      solution: [
        `Selecciona ${F("J5")}.`,
        `Escribe ${F('=SI(ESBLANCO([@Evaluación]),"Pendiente",[@Evaluación])')}.`,
      ],
      expectedResult: "Pendiente / A / B / C",
      finalFormula: '=SI(ESBLANCO([@Evaluación]),"Pendiente",[@Evaluación])',
    },
    {
      id: 3,
      level: "reto",
      title: "Tarea 3: Horas extra según la ley",
      description:
        conditions(
        "En la columna <strong>Pago horas extra</strong>. El valor de una hora es <strong>Salario diario ÷ 8</strong>:",
        [
          "Hasta <strong>9 horas</strong> extra → todas al <strong>doble</strong>",
          "Más de 9 → las primeras 9 al <strong>doble</strong> y las demás al <strong>triple</strong>",
        ],
        "Usa <strong>SI.CONJUNTO</strong> con <strong>VERDADERO</strong> para el segundo caso."
      ),
      solution: [
        `Selecciona ${F("K5")}.`,
        `Hasta 9 horas: ${F("[@[Horas extra]]*[@[Salario diario]]/8*2")}.`,
        `Más de 9: las 9 primeras al doble más el excedente al triple: ${F("9*[@[Salario diario]]/8*2+([@[Horas extra]]-9)*[@[Salario diario]]/8*3")}.`,
        `Escribe ${F("=SI.CONJUNTO([@[Horas extra]]<=9,[@[Horas extra]]*[@[Salario diario]]/8*2,VERDADERO,9*[@[Salario diario]]/8*2+([@[Horas extra]]-9)*[@[Salario diario]]/8*3)")}.`,
      ],
      expectedResult: "Pago en pesos por las horas extra",
      finalFormula:
        "=SI.CONJUNTO([@[Horas extra]]<=9,[@[Horas extra]]*[@[Salario diario]]/8*2,VERDADERO,9*[@[Salario diario]]/8*2+([@[Horas extra]]-9)*[@[Salario diario]]/8*3)",
    },
    {
      id: 4,
      level: "reto",
      title: "Tarea 4: Prima de antigüedad",
      description:
        conditions(
        "En la columna <strong>Prima antigüedad</strong>, reciben <strong>12 días de salario</strong> quienes cumplan <strong>cualquiera</strong> de estos casos:",
        [
          "<strong>5 años o más</strong> de antigüedad",
          "<strong>3 años o más</strong> <em>y</em> evaluación <strong>A</strong>",
        ],
        "Los demás reciben <strong>0</strong>."
      ),
      solution: [
        `Selecciona ${F("L5")}.`,
        `La regla es: antigüedad ≥ 5 O (antigüedad ≥ 3 Y evaluación A). Es un ${F("Y(...)")} dentro de un ${F("O(...)")}.`,
        `Escribe ${F('=SI(O([@Antigüedad]>=5,Y([@Antigüedad]>=3,[@Evaluación]="A")),[@[Salario diario]]*12,0)')}.`,
      ],
      expectedResult: "12 días de salario, o 0",
      finalFormula: '=SI(O([@Antigüedad]>=5,Y([@Antigüedad]>=3,[@Evaluación]="A")),[@[Salario diario]]*12,0)',
    },
    {
      id: 5,
      level: "reto",
      title: "Tarea 5: Indicador de productividad",
      description:
        conditions(
        "En la columna <strong>Productividad</strong>, según Ventas ÷ Meta:",
        [
          "<strong>110% o más</strong> → <strong>Sobresaliente</strong>",
          "<strong>100% o más</strong> → <strong>Cumple</strong>",
          "<strong>Menos</strong> → <strong>No cumple</strong>",
          "El personal administrativo tiene meta 0 y la división da error → <strong>Sin meta</strong>",
        ]
      ),
      solution: [
        `Selecciona ${F("M5")}.`,
        "Arma primero el SI.CONJUNTO con las tres categorías y después envuélvelo con SI.ERROR.",
        `Escribe ${F('=SI.ERROR(SI.CONJUNTO([@Ventas]/[@Meta]>=110%,"Sobresaliente",[@Ventas]/[@Meta]>=100%,"Cumple",VERDADERO,"No cumple"),"Sin meta")')}.`,
      ],
      expectedResult: "Sobresaliente / Cumple / No cumple / Sin meta",
      finalFormula:
        '=SI.ERROR(SI.CONJUNTO([@Ventas]/[@Meta]>=110%,"Sobresaliente",[@Ventas]/[@Meta]>=100%,"Cumple",VERDADERO,"No cumple"),"Sin meta")',
    },
  ],
};
